import React, { forwardRef, useContext, useId, useState } from 'react'
import { Button, ButtonModel, IconRegularBadge, Ratings } from '@notino/react-styleguide'
import { FormattedMessage, useIntl } from 'react-intl'
import dayjs from 'dayjs'

// types
import { SalonDetail, SalonPageQuery, SalonReviewsResponse } from '../../../../types/types'

// utils
import { AppContext } from '../../../../utils/appProvider'
import { RATING_ID } from '../../../../utils/enums'
import { formatRating, formatSalonReviewDate } from '../../../../utils/helper'

// components
import SalonRatings from '../SalonRatings/SalonRatings'
import SalonPartialRatings from '../../../../components/SalonPartialRatings/SalonPartialRatings'
import Tooltip from '../../../../components/Tooltip/Tooltip'
import VisuallyHidden from '../../../../atoms/VisuallyHidden/VisuallyHidden'

// hooks
import useMessages from '../../../../hooks/useMessages'
import useSalonReviews from '../../../../hooks/useSalonReviews'

// styles
import * as SC from './SalonPageReviewsStyles'

type SalonReviewsState = ReturnType<typeof useSalonReviews>

type Props = {
	salonData: SalonDetail
	reviewsData: SalonReviewsResponse
	query: SalonPageQuery
	ratingComponentIdPrefix: string
	onOpenAddReview: () => void
	reviews: SalonReviewsState['reviews']
	reviewsPage: SalonReviewsState['reviewsPage']
	isLoadingReviews: SalonReviewsState['isLoadingReviews']
	fetchMoreReviews: SalonReviewsState['fetchMoreReviews']
}

const getReviewDateTimeAttribute = (dateInput: string | number | Date) => {
	const date = dayjs(dateInput)
	return date.isValid() ? date.toISOString() : undefined
}

const SalonPageReviews = forwardRef<HTMLElement, Props>((props, ref) => {
	const intl = useIntl()
	const { messages } = useMessages()
	const { isAuthorized } = useContext(AppContext)
	const reviewsHeadingId = useId()

	const { salonData, reviewsData, query, ratingComponentIdPrefix, onOpenAddReview, reviews, reviewsPage, isLoadingReviews, fetchMoreReviews } = props

	const [reviewsStatusMessage, setReviewsStatusMessage] = useState('')

	const { totalPages } = reviewsData.pagination
	const hasMoreReviews = totalPages ? reviewsPage < totalPages : false

	/** review is allowed to add
	 * - logged in user
	 * - user with a valid token in the URL (when guest user completes the reservation in the salon, an email with URL for providing a review for the reservation is sent to them. Shape of the URL should be: "salony/{seoSlugName}/?reservationID={reservationID}&t={token}")
	 */
	const isUserAllowedToSeeReviews = isAuthorized || query.t

	const getReviewButton = (buttonProps: React.ComponentProps<typeof Button> = {}) => {
		return (
			<Button fullWidth buttonStyle={ButtonModel.Styles.secondary} onClick={onOpenAddReview} {...buttonProps}>
				{messages['Add review']}
			</Button>
		)
	}

	const handleShowMoreReviews = async () => {
		setReviewsStatusMessage(messages['Loading more reviews'])

		const result = await fetchMoreReviews()

		if (result.success) {
			setReviewsStatusMessage(
				intl.formatMessage(
					{
						id: '{count, plural, one {# more review was loaded} other {# more reviews were loaded}}',
						defaultMessage: '{count, plural, one {# more review was loaded} other {# more reviews were loaded}}'
					},
					{ count: result.loadedCount }
				)
			)
		} else {
			setReviewsStatusMessage(messages['Something went wrong. Data was not loaded.'])
		}
	}

	const getStarsAriaLabel = (starCount: number) =>
		intl.formatMessage(
			{
				id: '{starCount, plural, one {# star} other {# stars}}',
				defaultMessage: '{starCount, plural, one {# star} other {# stars}}'
			},
			{ starCount }
		)

	const averageRatingValue = salonData.ratingsData.rating
	const averageRatingValueText = intl.formatMessage(
		{
			id: '{rating} out of 5, {reviewCount, plural, one {# review} other {# reviews}}',
			defaultMessage: '{rating} out of 5, {reviewCount, plural, one {# review} other {# reviews}}'
		},
		{
			rating: averageRatingValue !== undefined ? formatRating(intl, averageRatingValue) : formatRating(intl, 0),
			reviewCount: salonData.ratingsData.ratesCount
		}
	)

	return (
		<SC.SalonReviewsContainer ref={ref} aria-labelledby={reviewsHeadingId}>
			<SC.Heading id={reviewsHeadingId}>{messages.Reviews}</SC.Heading>
			<SC.ReviewsHeader>
				<SC.ReviewsHeaderTop>
					<SC.HeaderLeftColumn>
						<SC.AverageRatingBlock>
							<VisuallyHidden as={'h3'}>{messages['Average rating']}</VisuallyHidden>
							<VisuallyHidden>{averageRatingValueText}</VisuallyHidden>
							<SC.AverageRatingContainer aria-hidden={'true'}>
								{averageRatingValue !== undefined && <SC.AverageRatingValue>{formatRating(intl, averageRatingValue)}</SC.AverageRatingValue>}
								<Ratings
									ratingId={`${ratingComponentIdPrefix}-${RATING_ID.SALON_DETAIL_PAGE_REVIEWS_REVIEW_AVERAGE}`}
									foreground={'text.primary'}
									background={'icon.disabled'}
									rating={salonData.ratingsData.rating}
									size={20}
								/>
								<SC.TotalRatingsCount>
									<FormattedMessage
										id={'({ totalCount } reviews)'}
										defaultMessage={'({totalCount, plural, one {{totalCount} review} other {{totalCount} reviews}})'}
										values={{
											totalCount: salonData.ratingsData.ratesCount
										}}
									/>
								</SC.TotalRatingsCount>
							</SC.AverageRatingContainer>
						</SC.AverageRatingBlock>
						{isUserAllowedToSeeReviews && <SC.AddReviewsButtonDesktop>{getReviewButton()}</SC.AddReviewsButtonDesktop>}
					</SC.HeaderLeftColumn>
					<SC.GroupedRatingsContainer>
						<VisuallyHidden as={'h3'}>{intl.formatMessage({ id: 'Rating distribution', defaultMessage: 'Rating distribution' })}</VisuallyHidden>
						<SalonRatings ratingsData={salonData.ratingsData} />
					</SC.GroupedRatingsContainer>
				</SC.ReviewsHeaderTop>
				<SC.PartialRatingsContainer>
					<VisuallyHidden as={'h3'}>{intl.formatMessage({ id: 'Ratings by category', defaultMessage: 'Ratings by category' })}</VisuallyHidden>
					<SalonPartialRatings value={salonData.ratingsData.partialRatings} readOnly roundReadOnlyValues />
				</SC.PartialRatingsContainer>
				{isUserAllowedToSeeReviews && <SC.AddReviewsButtonMobile>{getReviewButton({ fullWidth: true })}</SC.AddReviewsButtonMobile>}
			</SC.ReviewsHeader>
			{reviews && (
				<>
					<VisuallyHidden as={'h3'}>{messages['Customer reviews']}</VisuallyHidden>
					<VisuallyHidden as={'div'} role={'status'} aria-live={'polite'} aria-atomic={'true'}>
						{reviewsStatusMessage}
					</VisuallyHidden>
					<SC.ReviewsContainer>
						{reviews.map((review) => {
							return (
								<SC.ReviewContainer key={review.id}>
									<SC.Review>
										<SC.ReviewInfo>
											<SC.ReviewInfoLeftContent>
												<SC.StyledRatings
													ratingId={`${ratingComponentIdPrefix}-${RATING_ID.SALON_DETAIL_PAGE_REVIEWS_REVIEW}-${review.id}`}
													foreground={'text.primary'}
													background={'icon.disabled'}
													rating={review.rating}
													size={14}
													aria-label={getStarsAriaLabel(Math.round(review.rating))}
												/>
												<SC.ReviewerName
													aria-label={intl.formatMessage(
														{ id: 'Review by {name}', defaultMessage: 'Review by {name}' },
														{ name: review.reviewerName }
													)}
												>
													{review.reviewerName}
												</SC.ReviewerName>
												{review.verified && (
													<Tooltip
														content={
															<SC.VerifiedReviewTooltipContent>
																{messages['The customer wrote the review after completing a service in the salon.']}
															</SC.VerifiedReviewTooltipContent>
														}
														styles={{ body: { maxWidth: 250 } }}
													>
														<SC.VerifiedReview>
															<IconRegularBadge color={'text.positive'} width={'1rem'} height={'1rem'} aria-hidden />
															<span>{messages['Verified review']}</span>
															<VisuallyHidden>
																{messages['The customer wrote the review after completing a service in the salon.']}
															</VisuallyHidden>
														</SC.VerifiedReview>
													</Tooltip>
												)}
											</SC.ReviewInfoLeftContent>
											<SC.ReviewDate dateTime={getReviewDateTimeAttribute(review.createdAt)}>
												{formatSalonReviewDate(review.createdAt, intl)}
											</SC.ReviewDate>
										</SC.ReviewInfo>
										<SC.ReviewMessage>{review.reviewMessage}</SC.ReviewMessage>
									</SC.Review>
									{review.salonResponse?.message && (
										<SC.ReviewResponse>
											<VisuallyHidden as={'h5'}>{messages['Salon response']}</VisuallyHidden>
											<SC.ReviewResponseInfo>
												<SC.SalonName>{salonData.name || salonData.id}</SC.SalonName>
												<SC.ReviewResponseDate dateTime={getReviewDateTimeAttribute(review.salonResponse.updatedAt)}>
													{formatSalonReviewDate(review.salonResponse.updatedAt, intl)}
												</SC.ReviewResponseDate>
											</SC.ReviewResponseInfo>
											<SC.ResponseMessage>{review.salonResponse.message}</SC.ResponseMessage>
										</SC.ReviewResponse>
									)}
								</SC.ReviewContainer>
							)
						})}
					</SC.ReviewsContainer>
					{hasMoreReviews && (
						<SC.ReviewsButtonContainer>
							<Button
								onClick={handleShowMoreReviews}
								buttonStyle={ButtonModel.Styles.secondary}
								disabled={isLoadingReviews}
								aria-label={messages['Show more reviews']}
							>
								{messages['Show more']}
							</Button>
						</SC.ReviewsButtonContainer>
					)}
				</>
			)}
		</SC.SalonReviewsContainer>
	)
})

SalonPageReviews.displayName = 'SalonPageReviews'

export default SalonPageReviews
